import { get, ref } from "firebase/database";
import { db } from "../lib/firebase";
import types from "../types";
import { ROLES_SHOPS } from "../utils/constants";
import {
  filterFailedIntegrationsForModal,
  getCampaignIDsMissingStates,
} from "../utils/integrationNotFound";

export const setShowModalIntegrationNotFound = (open) => ({
  type: types.ALERTS_SHOW_MODAL_INTEGRATION_NOT_FOUND,
  payload: open,
});

export const setIntegrationsNotFound = (list) => ({
  type: types.ALERTS_SET_INTEGRATIONS_NOT_FOUND,
  payload: list,
});

const getPathCampaignsFromUser = (user) => {
  if (ROLES_SHOPS.includes(user?.role) && user?.shopID) {
    return `adsCampaigns/${user.shopID}`;
  }
  if (user?.advertiserID) {
    return `adsCampaigns/${user.advertiserID}`;
  }
  if (user?.shopID) {
    return `adsCampaigns/${user.shopID}`;
  }
  return "";
};

const fetchCampaignStatesFromRtdb = async (pathCampaigns, campaignIDs) => {
  const entries = await Promise.all(
    campaignIDs.map(async (campaignID) => {
      try {
        const snapshot = await get(
          ref(db, `${pathCampaigns}/${campaignID}/states`)
        );
        if (!snapshot.exists()) {
          return [campaignID, null];
        }
        return [campaignID, snapshot.val() ?? null];
      } catch {
        return [campaignID, null];
      }
    })
  );

  return Object.fromEntries(entries);
};

const mergeCampaignStates = (campaigns, statesByCampaignID) => {
  const nextCampaigns = { ...campaigns };

  Object.entries(statesByCampaignID).forEach(([campaignID, states]) => {
    if (states == null) return;
    nextCampaigns[campaignID] = {
      ...nextCampaigns[campaignID],
      states,
    };
  });

  return nextCampaigns;
};

/**
 * When an endpoint returns ok: true but includes failedIntegrations (partial
 * platform failures), show the same modal as integration-not-found errors.
 * Does not replace an alert that is already open or has pending items.
 * Only active campaigns enter. Missing Redux states are resolved from RTDB
 * before deciding. forceShow bypasses the status gate for activation and for
 * report or campaign-statistics calls.
 */
export const openIntegrationNotFoundModalIfNeeded =
  (failedIntegrations, { forceShow = false } = {}) =>
  async (dispatch, getState) => {
    if (!Array.isArray(failedIntegrations) || failedIntegrations.length === 0) {
      return false;
    }

    const state = getState();
    const current = state?.alerts?.integrationNotFound;
    if (current?.open || current?.items?.length > 0) return true;

    if (forceShow) {
      dispatch(setIntegrationsNotFound(failedIntegrations));
      dispatch(setShowModalIntegrationNotFound(true));
      return true;
    }

    let campaigns = { ...(state?.adsCampaigns?.adsCampaigns || {}) };
    const statusOverrides = state?.adsCampaigns?.statusOverrides || {};
    const missingCampaignIDs = getCampaignIDsMissingStates({
      items: failedIntegrations,
      campaigns,
    });

    if (missingCampaignIDs.length > 0) {
      const pathCampaigns = getPathCampaignsFromUser(state?.auth?.user);
      if (!pathCampaigns) {
        return false;
      }

      const statesByCampaignID = await fetchCampaignStatesFromRtdb(
        pathCampaigns,
        missingCampaignIDs
      );
      campaigns = mergeCampaignStates(campaigns, statesByCampaignID);

      Object.entries(statesByCampaignID).forEach(([campaignID, states]) => {
        if (states == null) return;
        dispatch({
          type: types.GET_BASIC_DATA_ADS_CAMPAIGN,
          payload: {
            key: campaignID,
            data: { states },
          },
        });
      });
    }

    const latest = getState();
    const latestAlert = latest?.alerts?.integrationNotFound;
    if (latestAlert?.open || latestAlert?.items?.length > 0) return true;

    const items = filterFailedIntegrationsForModal({
      items: failedIntegrations,
      campaigns,
      statusOverrides: latest?.adsCampaigns?.statusOverrides || statusOverrides,
      forceShow,
    });
    if (items.length === 0) return false;

    dispatch(setIntegrationsNotFound(items));
    dispatch(setShowModalIntegrationNotFound(true));
    return true;
  };

export const resetIntegrationNotFound = () => ({
  type: types.ALERTS_RESET_INTEGRATION_NOT_FOUND,
});

export const setPendingIntegrationFormSync = (payload) => ({
  type: types.ALERTS_SET_PENDING_INTEGRATION_FORM_SYNC,
  payload,
});

export const consumePendingIntegrationFormSyncForCampaign = (campaignID) => ({
  type: types.ALERTS_CONSUME_PENDING_INTEGRATION_FORM_SYNC_FOR_CAMPAIGN,
  payload: campaignID,
});

// Generic notification actions
export const addNotification = (notificationData) => ({
  type: types.ALERTS_ADD_NOTIFICATION,
  payload: notificationData,
});

export const updateNotification = (notificationID, updates) => ({
  type: types.ALERTS_UPDATE_NOTIFICATION,
  payload: {
    id: notificationID,
    updates,
  },
});

export const removeNotification = (notificationID) => ({
  type: types.ALERTS_REMOVE_NOTIFICATION,
  payload: {
    id: notificationID,
  },
});

export const clearAllNotifications = () => ({
  type: types.ALERTS_CLEAR_ALL_NOTIFICATIONS,
});

export const syncCreativeJobNotificationsAction = (notifications) => ({
  type: types.ALERTS_SYNC_CREATIVE_JOB_NOTIFICATIONS,
  payload: notifications,
});

export const promoteCreativeNotification = ({ fromId, jobID }) => ({
  type: types.ALERTS_PROMOTE_CREATIVE_NOTIFICATION,
  payload: { fromId, jobID },
});
